/**
 * What's new: a short list of the releases' action items (things with a button), plus
 * which of them this user has not opened yet (drives the purple dot). Add an entry
 * when something ships that needs the user to act; an entry added to the same version
 * later lights the dot again because "seen" is tracked per entry id.
 */

/**
 * Newest first. Two kinds of entry:
 * - action entries (a button the user can act on, e.g. Quick capture) always stay listed
 *   whatever the user answered, so there is a way back; visibleReleases hides the ones that
 *   don't apply to this user at all (e.g. Gemini notes without a Google Meet connector).
 *   Only these light the purple dot.
 * - `info: true` entries are plain version history for the important changes; always
 *   listed, never lit.
 *
 * Add a release here when the desktop version in package.json is bumped.
 *
 * @example
 * RELEASES[0].items[0].id; // "add-filter"
 */
export const RELEASES = [
  {
    version: "0.1.18",
    date: "2026-11-03",
    items: [
      {
        id: "add-filter",
        info: true,
        title: "Pin any filter to the sidebar",
        description: "Add filter: pick a source, category or AI Actions ready and keep it one click away.",
      },
      {
        id: "task-source-note",
        info: true,
        title: "Where a task came from",
        description: "Each task has a short Source note, and Read transcript opens the call right under it.",
      },
    ],
  },
  {
    version: "0.1.17",
    date: "2026-11-02",
    items: [
      {
        id: "meet-gemini-notes",
        title: "Gemini notes for Google Meet",
        description: "Also read Meet's own notes for calls that have no transcript.",
        tip: "Optional. Change it any time in Settings → Connectors → Google Meet.",
      },
    ],
  },
  {
    version: "0.1.16",
    date: "2026-10-30",
    items: [
      {
        id: "quick-capture",
        title: "Quick capture",
        description: "Select text in any app, then press Ctrl+Option+K to add it as a task.",
        tip: "Needs macOS Accessibility permission.",
      },
    ],
  },
  {
    version: "0.1.15",
    date: "2026-10-30",
    items: [
      {
        id: "ai-actions-assist",
        info: true,
        title: "AI Actions Assist",
        description: "Open a task and let AI draft the reply, Slack message or CRM update for you to review and send.",
      },
    ],
  },
  {
    version: "0.1.12",
    date: "2026-10-28",
    items: [
      {
        id: "notion-source",
        info: true,
        title: "Notion as a source",
        description: "Connect Notion to pull tasks from your meeting recordings.",
      },
    ],
  },
  {
    version: "0.1.10",
    date: "2026-09-11",
    items: [
      {
        id: "capture-notifications",
        info: true,
        title: "Capture notifications",
        description: "Choose how the desktop app tells you a capture was saved.",
      },
    ],
  },
  {
    version: "0.1.0",
    date: "2026-08-17",
    items: [
      {
        id: "desktop-app",
        info: true,
        title: "Desktop app",
        description: "Task Mantis for Mac, with sign-in handled in your browser and inline AI Assist editing.",
      },
    ],
  },
];

const SEEN_KEY_PREFIX = "starmesh.whatsNew.seen.v1:";

/**
 * localStorage key for one user's seen entry ids.
 *
 * @example
 * whatsNewSeenKey("u-1"); // "starmesh.whatsNew.seen.v1:u-1"
 */
export const whatsNewSeenKey = (userId) =>
  `${SEEN_KEY_PREFIX}${String(userId || "anon").trim() || "anon"}`;

/**
 * @example
 * readSeenWhatsNew("u-1"); // [] until the pane is opened
 */
export const readSeenWhatsNew = (userId) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(whatsNewSeenKey(userId)) || "[]");
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_) {
    return [];
  }
};

/**
 * Remember these entry ids as seen (merged with what was already seen).
 *
 * @example
 * markWhatsNewSeen("u-1", ["add-filter"]); // ["add-filter"]
 */
export const markWhatsNewSeen = (userId, ids) => {
  const next = Array.from(new Set([...readSeenWhatsNew(userId), ...(ids || []).map(String)]));
  try {
    window.localStorage.setItem(whatsNewSeenKey(userId), JSON.stringify(next));
  } catch (_) {
    // Storage blocked: the dot may return next visit, which is harmless.
  }
  return next;
};

/**
 * Releases with only the entries that apply to this user (history entries always do);
 * empty releases are dropped.
 *
 * @example
 * visibleReleases({ activeIds: ["quick-capture"] }); // no Gemini notes release, history kept
 */
export const visibleReleases = ({ activeIds = [] } = {}) => {
  const active = new Set(activeIds);
  return RELEASES.map((release) => ({
    ...release,
    items: release.items.filter((item) => item.info || active.has(item.id)),
  })).filter((release) => release.items.length > 0);
};

/**
 * Ids of action entries the user has not opened yet (history entries never count).
 *
 * @example
 * unseenWhatsNewIds(visibleReleases(), ["add-filter"]); // every other entry id
 */
export const unseenWhatsNewIds = (releases, seenIds = []) => {
  const seen = new Set(seenIds);
  return releases.flatMap((release) => release.items.filter((item) => !item.info).map((item) => item.id)).filter((id) => !seen.has(id));
};

/**
 * The newest version, shown next to "What's new" in the menu.
 *
 * @example
 * latestWhatsNewVersion(visibleReleases()); // "0.1.18"
 */
export const latestWhatsNewVersion = (releases) => releases[0]?.version || "";
